<template>
  <oxd-dialog
    :style="{width: '90%', maxWidth: '1050px'}"
    @update:show="onCancel"
    ><div class="gsl-hrm-modal-header">
      <oxd-text type="card-title">
        {{ $t('pim.payslip') }}
      </oxd-text>
    </div>
    <oxd-divider />
    <br />
    <div v-if="!isLoading && Object.keys(payrollDetails).length">
      <oxd-text type="card-title">
        {{ $t('pim.payroll_details') }}
      </oxd-text>
      <div class="gsl-hrm-personal-details">
        <div
          v-for="(value, key) in payrollDetails"
          :key="key"
          class="detail-row"
        >
          <strong>{{ formatKey(key) }}:</strong> {{ value }}
        </div>
      </div>
    </div>
    <oxd-form :loading="isLoading" @submit-valid="onSave">
      <br />
      <oxd-form-row style="padding-left: 10px; padding-right: 10px">
        <oxd-grid :cols="3" class="gsl-hrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="payslip.payFrequency"
              type="select"
              :label="$t('pim.pay_frequency')"
              :options="payFrequencyList"
              :rules="rules.payFrequency"
              required
            />
          </oxd-grid-item>

          <oxd-grid-item v-if="showWeekSelector">
            <oxd-input-field
              v-model="payslip.payPeriod.week"
              type="select"
              :label="$t('general.week')"
              :options="weeks"
              :rules="rules.payPeriod.week"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="payslip.payPeriod.month"
              type="select"
              :options="months"
              :rules="rules.payPeriod.month"
              :label="$t('general.month')"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="payslip.payPeriod.year"
              type="select"
              :options="years"
              :rules="rules.payPeriod.year"
              :label="$t('general.year')"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="payslip.paidDays"
              :label="$t('general.paid_days')"
              :rules="rules.paidDays"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="payslip.lossOfPay"
              :label="$t('general.loss_of_pay')"
              :rules="rules.lossOfPay"
              required
            />
          </oxd-grid-item>

          <oxd-grid-item>
            <oxd-input-field
              v-model="payslip.currency"
              type="select"
              :label="$t('general.currency')"
              :options="currencyList"
              :rules="rules.currency"
              required
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>

      <br />
      <oxd-text type="card-title"> {{ $t('pim.salary_components') }} </oxd-text>
      <br />
      <oxd-card-table
        v-model:selected="checkedSalaryComponentItems"
        :selector="selector"
        :headers="headers"
        :items="salaryComponents"
        :mobile-checkbox="true"
        selectable
        class="gsl-hrm-payslip-dialog-table"
      >
      </oxd-card-table>
      <br />
      <oxd-text type="card-title">
        {{ $t('pim.salary_deduction_components') }}
      </oxd-text>
      <br />
      <oxd-card-table
        v-model:selected="checkedSalaryDeductionItems"
        :selector="selector"
        :headers="headers"
        :items="deductionComponents"
        :mobile-checkbox="true"
        selectable
        class="gsl-hrm-payslip-dialog-table"
      >
      </oxd-card-table>
      <br />
      <oxd-text type="card-title">
        {{ $t('pim.other_components') }}
      </oxd-text>
      <br />
      <oxd-card-table
        v-model:selected="checkedSalaryOtherItems"
        :selector="selector"
        :headers="headers"
        :items="otherComponents"
        :mobile-checkbox="true"
        selectable
        class="gsl-hrm-payslip-dialog-table"
      >
      </oxd-card-table>
      <br />

      <oxd-text type="card-title">
        {{ $t('pim.employer_liabilities') }}
      </oxd-text>
      <br />
      <oxd-card-table
        v-model:selected="checkedSalaryEmployerLiabilities"
        :selector="selector"
        :headers="headers"
        :items="employerLiabilities"
        :mobile-checkbox="true"
        selectable
        class="gsl-hrm-payslip-dialog-table"
      >
      </oxd-card-table>
      <br />
      <oxd-divider />
      <oxd-form-actions>
        <oxd-button
          type="button"
          display-type="ghost"
          :label="$t('general.cancel')"
          @click="onCancel"
        />
        <submit-button :label="$t('general.generate')" />
      </oxd-form-actions>
    </oxd-form>
  </oxd-dialog>
</template>
<script>
import {OxdDialog} from '@ohrm/oxd';
import {APIService} from '@/core/util/services/api.service';
import {OxdCardTable} from '@ohrm/oxd';
import ExcelJS from 'exceljs';
import {saveAs} from 'file-saver';
import {required, numericOnly} from '@ohrm/core/util/validation/rules';

import useDateFormat from '@/core/util/composable/useDateFormat';
import useLocale from '@/core/util/composable/useLocale';
import html2pdf from 'html2pdf.js';

export default {
  name: 'PayslipGenerateModal',
  components: {
    'oxd-dialog': OxdDialog,
    'oxd-card-table': OxdCardTable,
  },
  props: {
    empNumber: {
      type: [Number, String],
      required: true,
    },
  },
  emits: ['close'],
  setup(props) {
    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/pim/employees/${props.empNumber}`,
    );

    const httpForAttachment = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/pim/employees/${props.empNumber}/screen/salary/attachments`,
    );

    const {jsDateFormat} = useDateFormat();
    const {locale} = useLocale();
    return {
      http,
      jsDateFormat,
      locale,
      httpForAttachment,
    };
  },
  data() {
    return {
      isLoading: false,
      showWeekSelector: false,
      personalDetails: {},
      payrollDetails: {},
      jobDetails: {},
      salaryComponents: [],
      deductionComponents: [],
      otherComponents: [],
      employerLiabilities: [],
      payslip: {
        currency: null,
        payFrequency: null,
        payPeriod: {
          week: null,
          month: null,
          year: null,
        },
        lossOfPay: null,
        paidDays: null,
      },

      currencyList: [
        {id: 1, label: 'USD', value: '$'},
        {id: 2, label: 'EUR', value: '€'},
        {id: 3, label: 'GBP', value: '£'},
        {id: 4, label: 'JPY', value: '¥'},
        {id: 5, label: 'AUD', value: 'A$'},
        {id: 6, label: 'CAD', value: 'C$'},
        {id: 7, label: 'CHF', value: 'CHF'},
        {id: 8, label: 'CNY', value: '¥'},
        {id: 9, label: 'HKD', value: 'HK$'},
        {id: 10, label: 'INR', value: '₹'},
        {id: 11, label: 'KRW', value: '₩'},
        {id: 12, label: 'SGD', value: 'S$'},
        {id: 13, label: 'NZD', value: 'NZ$'},
        {id: 14, label: 'ZAR', value: 'R'},
        {id: 15, label: 'BRL', value: 'R$'},
        {id: 16, label: 'RUB', value: '₽'},
        {id: 17, label: 'MXN', value: '$'},
        {id: 18, label: 'IDR', value: 'Rp'},
        {id: 19, label: 'TRY', value: '₺'},
        {id: 20, label: 'SAR', value: '﷼'},
        {id: 21, label: 'AED', value: 'د.إ'},
        {id: 22, label: 'THB', value: '฿'},
        {id: 23, label: 'VND', value: '₫'},
        {id: 24, label: 'MYR', value: 'RM'},
        {id: 25, label: 'NGN', value: '₦'},
        {id: 26, label: 'PKR', value: '₨'},
        {id: 27, label: 'EGP', value: '£'},
        {id: 28, label: 'PLN', value: 'zł'},
        {id: 29, label: 'SEK', value: 'kr'},
        {id: 30, label: 'NOK', value: 'kr'},
        {id: 31, label: 'DKK', value: 'kr'},
        {id: 32, label: 'CZK', value: 'Kč'},
        {id: 33, label: 'HUF', value: 'Ft'},
        {id: 34, label: 'ILS', value: '₪'},
        {id: 35, label: 'QAR', value: 'ر.ق'},
        {id: 36, label: 'KWD', value: 'د.ك'},
        {id: 37, label: 'BHD', value: 'ب.د'},
        {id: 38, label: 'OMR', value: 'ر.ع.'},
        {id: 39, label: 'JOD', value: 'د.ا'},
        {id: 40, label: 'BDT', value: '৳'},
        {id: 41, label: 'LKR', value: 'Rs'},
        {id: 42, label: 'TWD', value: 'NT$'},
      ],
      payFrequencyList: [
        {id: 'weekly', label: 'Weekly'},
        {id: 'monthly', label: 'Monthly'},
      ],

      selector: {
        style: {},
      },
      headers: [
        {name: 'name', title: 'Name', style: {flex: 5}},
        {name: 'amount', title: 'Amount', style: {flex: 1}},
      ],

      checkedSalaryComponentItems: [],
      checkedSalaryDeductionItems: [],
      checkedSalaryOtherItems: [],
      checkedSalaryEmployerLiabilities: [],
      rules: {
        payFrequency: [required],
        currency: [required],
        payPeriod: {
          week: [required],
          month: [required],
          year: [required],
        },
        lossOfPay: [numericOnly],
        paidDays: [numericOnly],
      },
    };
  },

  computed: {
    months() {
      return Array(12)
        .fill('')
        .map((...[, index]) => {
          return {
            id: index + 1,
            label: this.locale.localize.month(index, {
              width: 'wide',
            }),
          };
        });
    },
    years() {
      const currentYear = new Date().getFullYear();
      return Array.from({length: 10}, (_, i) => {
        const year = currentYear - 5 + i;
        return {id: year, label: year.toString()};
      });
    },

    weeks() {
      return Array.from({length: 5}, (_, i) => ({
        id: i + 1,
        label: `${this.$t('general.week')} ${i + 1}`,
      }));
    },
  },
  watch: {
    'payslip.payFrequency'(newVal) {
      this.showWeekSelector = newVal?.id === 'weekly';
      if (newVal?.id !== 'weekly') {
        this.payslip.payPeriod.week = null; // reset week if not weekly
      }
    },
  },
  beforeMount() {
    this.isLoading = true;

    const salaryRequest = this.http.request({
      method: 'GET',
      url: `/api/v2/pim/employees/${this.empNumber}/salary-components`,
    });

    const deductionRequest = this.http.request({
      method: 'GET',
      url: `/api/v2/pim/employees/${this.empNumber}/salary-deductions`,
    });

    const otherComponentRequest = this.http.request({
      method: 'GET',
      url: `/api/v2/pim/employees/${this.empNumber}/salary-other-components`,
      params: {
        type: 0,
      },
    });

    const otherEmployerLiabilitiesComponentRequest = this.http.request({
      method: 'GET',
      url: `/api/v2/pim/employees/${this.empNumber}/salary-other-components`,
      params: {
        type: 1,
      },
    });

    const payrollDetailsRequest = this.http.request({
      method: 'GET',
      url: `/api/v2/pim/employees/${this.empNumber}/payrolls`,
    });

    const personalDetailsRequest = this.http.request({
      method: 'GET',
      url: `/api/v2/pim/employees/${this.empNumber}/personal-details`,
    });

    const jobDetailsRequest = this.http.request({
      method: 'GET',
      url: `/api/v2/pim/employees/${this.empNumber}/job-details`,
    });

    Promise.all([
      salaryRequest,
      deductionRequest,
      payrollDetailsRequest,
      otherComponentRequest,
      personalDetailsRequest,
      jobDetailsRequest,
      otherEmployerLiabilitiesComponentRequest,
    ])
      .then(
        ([
          salaryRes,
          deductionRes,
          detailsRes,
          otherRes,
          personalRes,
          jobRes,
          employerRes,
        ]) => {
          const salaryData = salaryRes.data?.data || [];
          const deductionData = deductionRes.data?.data || [];
          const detailsData = detailsRes.data?.data || {};
          const otherData = otherRes.data?.data || [];
          const employerData = employerRes?.data?.data || [];
          this.personalDetails = personalRes.data?.data || {};
          this.jobDetails = jobRes.data?.data || {};

          this.salaryComponents = salaryData.map((item) => ({
            id: item.id,
            name: item.salaryName,
            amount: `${item.amount}`,
          }));

          this.deductionComponents = deductionData.map((item) => ({
            id: item.id,
            name: item.name,
            amount: `${item.amount}`,
          }));

          this.otherComponents = otherData.map((item) => ({
            id: item.id,
            name: item.name,
            type: item.type,
            amount: item.amount,
          }));

          this.employerLiabilities = employerData.map((item) => ({
            id: item.id,
            name: item.name,
            type: item.type,
            amount: item.amount,
          }));

          detailsData.forEach((item) => {
            this.payrollDetails[item.fieldKey] = item.fieldValue;
          });
        },
      )
      .finally(() => {
        this.isLoading = false;
      });
  },

  methods: {
    formatKey(key) {
      const countryFields = {
        IN: [
          {
            key: 'in_bank_account_number',
            label: 'Bank Account Number',
            required: true,
          },
          {key: 'in_ifsc_code', label: 'IFSC Number', required: true},
          {key: 'in_pan_number', label: 'PAN Number', required: true},
          {key: 'in_aadhaar_number', label: 'Aadhaar Number', required: false},
          {key: 'in_epf_uan', label: 'EPF UAN', required: false},
          {key: 'in_esic_number', label: 'ESIC Number', required: false},
        ],
        UK: [
          {
            key: 'uk_bank_account_number',
            label: 'Bank Account Number',
            required: true,
          },
          {key: 'uk_sort_code', label: 'Sort Code', required: true},
          {
            key: 'uk_ni_number',
            label: 'National Insurance Number',
            required: true,
          },
          {key: 'uk_tax_code', label: 'Tax Code', required: true},
          {
            key: 'uk_pension_scheme',
            label: 'Pension Scheme Reference',
            required: false,
          },
          {
            key: 'uk_student_loan_plan',
            label: 'Student Loan Plan Type',
            required: false,
          },
        ],
        US: [
          {
            key: 'us_bank_routing_number',
            label: 'Bank Routing Number',
            required: true,
          },
          {
            key: 'us_bank_account_number',
            label: 'Bank Account Number',
            required: true,
          },
          {key: 'us_state_tax_code', label: 'State Tax Code', required: false},
          {key: 'us_ssn', label: 'Social Security Number', required: true},
          {
            key: 'us_federal_tax_status',
            label: 'Federal Tax Filing Status',
            required: true,
          },
          {
            key: 'us_retirement_plan',
            label: '401(k)/Retirement Plan Contribution',
            required: false,
          },
        ],
        DE: [
          {
            key: 'sg_bank_account_number',
            label: 'Bank Account Number',
            required: true,
          },
          {key: 'de_iban', label: 'IBAN', required: true},
          {key: 'bic', label: 'BIC/SWIFT Code', required: true},
          {
            key: 'de_steuer_id',
            label: 'Tax Identification Number (Steuer-ID)',
            required: true,
          },
          {
            key: 'de_social_security_number',
            label: 'Social Security Number',
            required: true,
          },
          {
            key: 'de_health_insurance',
            label: 'Health Insurance Provider',
            required: false,
          },
        ],
        SG: [
          {key: 'sg_nric', label: 'NRIC/FIN Number', required: true},
          {
            key: 'sg_cpf_number',
            label: 'CPF Contribution Number',
            required: true,
          },
        ],
        XX: [
          {
            key: 'xx_bank_account_number',
            label: 'Bank Account Number',
            required: true,
          },
          {key: 'xx_iban', label: 'IBAN', required: false},
          {key: 'xx_swift_code', label: 'SWIFT/BIC Code', required: false},
          {
            key: 'xx_tax_id',
            label: 'Tax Identification Number (TIN)',
            required: true,
          },
          {
            key: 'xx_national_id',
            label: 'National ID / Social Security Number',
            required: true,
          },
        ],
      };

      // Search all country fields
      for (const country of Object.values(countryFields)) {
        const found = country.find((field) => field.key === key);
        if (found) return found.label;
      }

      // Fallback formatting if not found
      return key
        .replace(/^[a-z]{1,3}_/i, '') // strip prefix like "in_"
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase()); // capitalize
    },

    async onSave() {
      this.isLoading = true;

      const selectedSalary = this.checkedSalaryComponentItems.map(
        (i) => this.salaryComponents[i],
      );
      const selectedDeductions = this.checkedSalaryDeductionItems.map(
        (i) => this.deductionComponents[i],
      );
      const selectedOthersAdditions = this.checkedSalaryOtherItems.map(
        (i) => this.otherComponents[i],
      );

      const selectedEmployerLiabilities =
        this.checkedSalaryEmployerLiabilities.map(
          (i) => this.employerLiabilities[i],
        );

      // await this.generatePayslipExcel(
      //   this.payrollDetails,
      //   selectedSalary,
      //   selectedDeductions,
      //   selectedOthersAdditions,
      //   selectedEmployerLiabilities,
      // );

      await this.generatePDF(
        this.payrollDetails,
        selectedSalary,
        selectedDeductions,
        selectedOthersAdditions,
        selectedEmployerLiabilities,
      );
    },

    onCancel() {
      this.$emit('close');
    },

    applyCellStyle(
      cell,
      {
        bold = true,
        fontColor = 'FF000000',
        bgColor = 'FFDDDDDD',
        borderColor = 'FFB2B2B2',
      } = {},
    ) {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: {argb: bgColor},
      };
      cell.font = {
        name: 'Arial',
        size: 9,
        bold,
        color: {argb: fontColor},
      };
      cell.border = {
        top: {style: 'thin', color: {argb: borderColor}},
        bottom: {style: 'thin', color: {argb: borderColor}},
      };
    },

    totalRow(sheet, rowIndex, titles = {}) {
      sheet.spliceRows(rowIndex, 0, []);
      const row = sheet.getRow(rowIndex);
      ['B', 'C', 'D'].forEach((col) => {
        const cell = row.getCell(col);
        cell.value = titles[col]?.toUpperCase() || '';
        this.applyCellStyle(cell);
      });
      row.getCell('D').alignment = {horizontal: 'right', vertical: 'middle'};
      row.height = 20.97;
      row.commit();
    },

    insertSeparatorRow(sheet, rowIndex, titles = {}) {
      sheet.spliceRows(rowIndex, 0, []);
      const row = sheet.getRow(rowIndex);

      ['A', 'B', 'C', 'D'].forEach((col) => {
        const cell = row.getCell(col);
        cell.value = titles[col]?.toUpperCase() || '';
        const isDarker = col === 'D';
        this.applyCellStyle(cell, {
          bgColor: isDarker ? 'FFB2B2B2' : 'FFDDDDDD',
        });
      });

      row.getCell('A').alignment = {horizontal: 'left', vertical: 'middle'};
      row.getCell('C').alignment = {horizontal: 'right', vertical: 'middle'};
      row.getCell('D').alignment = {horizontal: 'right', vertical: 'middle'};
      row.height = 20.97;
      row.commit();
    },

    populateRows(sheet, data, startRow) {
      sheet.spliceRows(startRow, 0, ...new Array(data.length).fill([]));
      data.forEach((item, index) => {
        const currentRow = startRow + index;

        const row = sheet.getRow(startRow + index);
        row.getCell('A').value = item.name;
        row.getCell('C').value = item.value;
        // row.getCell('D').value = item.amount;

        sheet.mergeCells(`A${currentRow}:B${currentRow}`);

        row.height = 20.97;
        ['A', 'C', 'D'].forEach((col) => {
          row.getCell(col).alignment = {
            horizontal: col === 'A' ? 'left' : 'right',
            vertical: 'middle',
          };
        });
        row.getCell('D').numFmt = '+0.00;-0.00';
        row.commit();
      });
    },

    generatePayslipHtml(
      personalDetails,
      jobDetails,
      payslip,
      payrollDetails,
      selectedSalary,
      selectedDeductions,
      selectedOthersAdditions,
      selectedEmployerLiabilities,
      totalSalary,
      totalDeductions,
      totalOtherAddition,
      totalLiabilities,
      netPay,
      totalEmployerLiabilities,
    ) {
      const {
        firstName = '',
        middleName = '',
        lastName = '',
        employeeId = '',
      } = personalDetails;

      const fullName = [firstName, middleName, lastName]
        .filter(Boolean)
        .join(' ');
      const jobTitle = jobDetails.jobTitle?.title || '';
      // const joinedDate = jobDetails.joinedDate || '';

      const payPeriod = payslip.payPeriod || {};
      const payPeriodLabel = [
        payPeriod.week?.label,
        payPeriod.month?.label,
        payPeriod.year?.label,
      ]

        .filter(Boolean)
        .join(' ');
      const paidDays = payslip?.paidDays;
      const lossOfPay = payslip?.lossOfPay;
      const currency = payslip.currency?.value || '';

      // Helper function to build table rows
      const buildRows = (items) =>
        items
          .map(
            (item) =>
              `<tr>
                <td colspan="2" style="padding: 2px 8px; border-right: 1px solid #ccc;">${item.name}</td>
                <td style="text-align: right; padding: 2px 8px; border-right: 1px solid #ccc;">${currency}${item.amount}</td>
                <td style="padding: 4px 8px;"></td>
              </tr>`,
          )
          .join('');

      const totalRow = (label, currency, amount) => `
          <tr style="font-weight: bold;">
            <td></td>
            <td colspan="2" style="padding: 3px 8px; border: 1px solid #ccc;  background-color:#EEF5FF;">${label}</td>
            <td style="text-align: right; padding: 3px 8px; border-top: 1px solid #ccc; border-bottom: 1px solid #ccc;  background-color:#E9F2FF;">${currency}${amount}</td>
          </tr>
        `;

      const sectionHeader = (label) => `
          <tr style="background-color:#f0f0f0; font-weight: bold;">
            <td colspan="2" style="padding: 4px 8px; border-right: 1px solid #ccc;">${label}</td>
            <td style="text-align: right; padding: 4px 8px; border-right: 1px solid #ccc;">Amount</td>
            <td style="text-align: right; padding: 4px 8px;">Total</td>
          </tr>
        `;

      const sectionBlock = (
        label,
        items,
        currency,
        totalLabel,
        totalAmount,
      ) => `
        ${sectionHeader(label)}
        ${buildRows(items, currency)}
        ${totalRow(totalLabel, currency, totalAmount)}
      `;

      return `
      <div style="font-family: 'Segoe UI', Arial, sans-serif; font-size: 14px; color: #333; line-height: 1.5;">
        <h2 style="border-bottom: 2px solid #ccc; padding-bottom: 6px; margin-bottom: 10px;"></h2>

        <!-- Info Tables -->
        <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
          <div style="flex: 1; margin-right: 40px;">
            <table style="width: 100%; border-collapse: collapse;">
              <tbody>
                <tr><td><strong>Employee Name:</strong></td><td>${fullName}</td></tr>
                <tr><td><strong>Employee ID:</strong></td><td>${employeeId}</td></tr>
                <tr><td><strong>Designation:</strong></td><td>${jobTitle}</td></tr>
                <tr><td><strong>Pay Period:</strong></td><td>${payPeriodLabel}</td></tr>
                ${
                  paidDays &&
                  `
                  <tr><td><strong>Paid Days:</strong></td><td>${paidDays}</td></tr>
                `
                }
                ${
                  lossOfPay &&
                  `
                  <tr><td><strong>LOP Days:</strong></td><td>${lossOfPay}</td></tr>
                `
                }

              </tbody>
            </table>
          </div>
          <div style="flex: 1;">
            <table style="width: 100%; border-collapse: collapse;">
              <tbody>
                ${Object.entries(payrollDetails || {})
                  .map(
                    ([key, value]) => `
                    <tr>
                      <td><strong>${this.formatKey(key)}:</strong></td>
                      <td>${value}</td>
                    </tr>
                  `,
                  )
                  .join('')}
              </tbody>
            </table>
          </div>
        </div>

        <!-- Payslip Table -->
        <table style="width: 100%; border-collapse: collapse; font-size: 13px; border: 1px solid #ccc; border-radius: 10px; overflow: hidden;  margin-bottom: 8px">
          <colgroup>
            <col style="width: 25%;">
            <col style="width: 25%;">
            <col style="width: 25%;">
            <col style="width: 25%;">
          </colgroup>
          <tbody>

            <!-- Salary Components -->
            ${sectionHeader('Earnings')}
            ${buildRows(selectedSalary, currency)}
            ${totalRow('Gross Pay', currency, totalSalary.toFixed(2))}
          </tbody>
        </table>
        <table style="width: 100%; border-collapse: collapse; font-size: 13px; border: 1px solid #ccc; border-radius: 10px; overflow: hidden;  margin-bottom: 8px">
          <colgroup>
            <col style="width: 25%;">
            <col style="width: 25%;">
            <col style="width: 25%;">
            <col style="width: 25%;">
          </colgroup>
          <tbody>
            <!-- Deductions -->
            ${
              selectedDeductions.length > 0
                ? sectionBlock(
                    'Deductions',
                    selectedDeductions,
                    currency,
                    'Total Deductions',
                    totalDeductions.toFixed(2),
                  )
                : ''
            }
          </tbody>
        </table>
        <table style="width: 100%; border-collapse: collapse; font-size: 13px; border: 1px solid #ccc; border-radius: 10px; overflow: hidden;  margin-bottom: 8px">
          <colgroup>
            <col style="width: 25%;">
            <col style="width: 25%;">
            <col style="width: 25%;">
            <col style="width: 25%;">
          </colgroup>
          <tbody>


            <!-- Additions -->
            ${
              selectedOthersAdditions.length > 0
                ? sectionBlock(
                    'Additions',
                    selectedOthersAdditions,
                    currency,
                    'Total Additions',
                    totalOtherAddition.toFixed(2),
                  )
                : ''
            }
          </tbody>
        </table>

        <table style="width: 100%; border-collapse: collapse; font-size: 13px; border: 1px solid #ccc; border-radius: 10px; overflow: hidden; margin-bottom: 8px">
          <colgroup>
            <col style="width: 25%;">
            <col style="width: 25%;">
            <col style="width: 25%;">
            <col style="width: 25%;">
          </colgroup>
          <tbody>
            <!-- Net Pay -->
            <tr style="background-color:#DBFCE9FF; font-weight: bold; font-size: 14px;">
              <td></td>
              <td colspan="2" style="padding: 4px;">Net Pay
                <span style="display: block;font-size: 10px; color: grey; font-style: italic;">
                  Gross Pay - Total Deductions + (Total Additions)
                </span>
              </td>
              <td style="text-align: right; padding: 4px;">${currency}${netPay.toFixed(
        2,
      )}</td>
            </tr>
          </tbody>
        </table>

        <table style="width: 100%; border-collapse: collapse; font-size: 13px; border: 1px solid #ccc; border-radius: 10px; overflow: hidden; margin-bottom: 8px">
          <colgroup>
            <col style="width: 25%;">
            <col style="width: 25%;">
            <col style="width: 25%;">
            <col style="width: 25%;">
          </colgroup>
          <tbody>

            <!-- Employer Liabilities -->
            ${
              selectedEmployerLiabilities.length > 0
                ? sectionBlock(
                    'Employer Contributions',
                    selectedEmployerLiabilities,
                    currency,
                    'Total Employer Contributions',
                    totalEmployerLiabilities.toFixed(2),
                  )
                : ''
            }

          </tbody>
        </table>
        <table style="width: 100%; border-collapse: collapse; font-size: 13px; border: 1px solid #ccc; border-radius: 10px; overflow: hidden; margin-bottom: 4px">
          <colgroup>
            <col style="width: 25%;">
            <col style="width: 25%;">
            <col style="width: 25%;">
            <col style="width: 25%;">
          </colgroup>
          <tbody>
            <!-- Net Pay -->
            <tr style="background-color:#F0F0F0FF; font-weight: bold; font-size: 14px;">
              <td></td>
              <td colspan="2" style="padding: 4px;">CTC
                <span style="display: block; font-size: 10px; color: grey; font-style: italic;">
                  Total Gross Pay + Total Additions + Total Employer contributions
                </span>
              </td>
              <td style="text-align: right; padding: 4px;">${currency}${totalLiabilities.toFixed(
        2,
      )}</td>
            </tr>
          </tbody>
        </table>
        <div style="text-align: center; color: #969696FF; font-style: italic; margin-top: 5px; font-size: 10px">
          -- This is a system-generated document. --
        </div>
      </div>

    `;
    },

    generatePayslipFilename(payslip) {
      const {payFrequency, payPeriod} = payslip || {};
      if (!payFrequency || !payPeriod) return 'Payslip.pdf';

      const frequencyId = payFrequency.id?.toLowerCase();
      const year = payPeriod.year?.label || 'Year';

      if (frequencyId === 'monthly') {
        const month = payPeriod.month?.label || 'Month';
        return `Payslip-${month}-${year}.pdf`;
      }

      if (frequencyId === 'weekly') {
        const week = payPeriod.week?.id || '1';
        return `Payslip-Week${week}-${year}.pdf`;
      }

      return 'Payslip.pdf';
    },

    async generatePDF(
      payrollDetails,
      selectedSalary,
      selectedDeductions,
      selectedOthersAdditions,
      selectedEmployerLiabilities,
    ) {
      const totalSalary = this.calculateTotals(selectedSalary);
      const totalDeductions = this.calculateTotals(selectedDeductions);
      const totalOtherAddition = this.calculateTotals(selectedOthersAdditions);
      const totalEmployerLiabilities = this.calculateTotals(
        selectedEmployerLiabilities,
      );

      const netPay = totalSalary - totalDeductions + totalOtherAddition;
      const totalLiabilities =
        totalSalary + totalOtherAddition + totalEmployerLiabilities;

      const html = this.generatePayslipHtml(
        this.personalDetails,
        this.jobDetails,
        this.payslip,
        payrollDetails,
        selectedSalary,
        selectedDeductions,
        selectedOthersAdditions,
        selectedEmployerLiabilities,
        totalSalary,
        totalDeductions,
        totalOtherAddition,
        totalLiabilities,
        netPay,
        totalEmployerLiabilities,
      );

      const payPeriod = this.payslip?.payPeriod || {};
      const payPeriodLabel = [
        payPeriod.week?.label,
        payPeriod.month?.label,
        payPeriod.year?.label,
      ]
        .filter(Boolean)
        .join('-');

      const headerResponse = await fetch(
        `${window.appGlobal.baseUrl}/core/payslip-header/${payPeriodLabel}`,
      );
      const headerHtml = await headerResponse.json();

      const container = document.createElement('div');
      container.innerHTML = `${headerHtml?.html || ''}${html}`;
      document.body.appendChild(container);

      const pdfBlob = await html2pdf()
        .set({
          margin: 10,
          image: {type: 'jpeg', quality: 0.98},
          html2canvas: {scale: 2},
          jsPDF: {unit: 'mm', format: 'a4', orientation: 'portrait'},
        })
        .from(container)
        .output('blob');

      document.body.removeChild(container);

      const base64 = await this.blobToBase64(pdfBlob);
      const filename = this.generatePayslipFilename(this.payslip);
      const password = this.getPdfPassword(this.personalDetails);

      const attachment = {
        name: filename,
        type: 'application/pdf',
        base64: base64.split(',')[1],
        size: pdfBlob.size,
      };
      this.httpForAttachment
        .create({
          attachment: attachment,
          salaryPassword: password,
        })
        .then(() => {
          this.onCancel();
          return this.$toast.saveSuccess();
        });
    },

    calculateTotals(items) {
      return items.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    },

    getPdfPassword(details) {
      const lastName = details?.lastName || '';
      const dob = (details?.birthday || '').replace(/-/g, '');
      return `${lastName}${dob}`;
    },

    blobToBase64(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
      });
    },

    async generatePayslipExcel(
      payrollDetails,
      selectedSalary,
      selectedDeductions,
      selectedOthersAdditions,
      selectedEmployerLiabilities,
    ) {
      const totalSalary = selectedSalary.reduce(
        (sum, item) => sum + parseFloat(item.amount),
        0,
      );
      const totalDeductions = selectedDeductions.reduce(
        (sum, item) => sum + parseFloat(item.amount),
        0,
      );
      const totalOtherAddition = selectedOthersAdditions.reduce(
        (sum, item) => sum + parseFloat(item.amount),
        0,
      );
      const netPay = totalSalary - totalDeductions + totalOtherAddition;

      const totalEmployerLiabilities = selectedEmployerLiabilities.reduce(
        (sum, item) => sum + parseFloat(item.amount),
        0,
      );
      const totalLiabilities =
        totalSalary + totalOtherAddition + totalEmployerLiabilities;

      const workbook = new ExcelJS.Workbook();
      const response = await fetch('../../../../dist/payslip-template.xlsx');
      const buffer = await response.arrayBuffer();
      await workbook.xlsx.load(buffer);

      const sheet = workbook.getWorksheet(1);

      // First Part
      // Destructure personal and job details
      const {
        firstName = '',
        middleName = '',
        lastName = '',
        employeeId = '',
      } = this.personalDetails || {};

      const fullName = [firstName, middleName, lastName]
        .filter(Boolean)
        .join(' ');
      const jobTitle = this.jobDetails?.jobTitle?.title || '';
      const joinedDate = this.jobDetails?.joinedDate || '';

      // Format pay period label
      const payPeriod = this.payslip?.payPeriod || {};
      const payPeriodLabel = [
        payPeriod.week?.label,
        payPeriod.month?.label,
        payPeriod.year?.label,
      ]
        .filter(Boolean)
        .join(' ');

      // Define cell mappings
      const employeeDetails = [
        ['Employee Name', fullName],
        ['Employee Id', employeeId],
        ['Designation', jobTitle],
        ['Payable Days', ''],
        ['Date of Joining', joinedDate],
        ['Pay period', payPeriodLabel],
      ];

      // Populate cells starting from row 7
      employeeDetails.forEach(([label, value], index) => {
        const row = 7 + index;
        sheet.getCell(`A${row}`).value = label;
        sheet.getCell(`B${row}`).value = value;
      });

      // Second Part

      const detailKeys = Object.keys(payrollDetails || {});

      const layout = [
        ['C7', 'D7'],
        ['C8', 'D8'],
        ['C9', 'D9'],
        ['C10', 'D10'],
        ['C11', 'D11'],
        ['C12', 'D12'],
      ];

      layout.forEach(([keyCell, valueCell], index) => {
        if (detailKeys[index]) {
          sheet.getCell(keyCell).value = this.formatKey(detailKeys[index]);
          sheet.getCell(valueCell).value = payrollDetails[detailKeys[index]];
        }
      });

      let rowIndex = 15;

      this.populateRows(sheet, selectedSalary, rowIndex);
      rowIndex += selectedSalary.length;

      this.totalRow(sheet, rowIndex++, {
        B: 'GROSS PAY',
        D: this.payslip.currency?.value + totalSalary.toString(),
      });

      this.insertSeparatorRow(sheet, rowIndex++, {
        A: 'Deductions',
        C: 'Amount',
        D: 'Total',
      });

      this.populateRows(sheet, selectedDeductions, rowIndex);
      rowIndex += selectedDeductions.length;

      this.totalRow(sheet, rowIndex++, {
        B: 'Total deductions',
        D: this.payslip.currency?.value + totalDeductions.toString(),
      });

      if (selectedOthersAdditions.length > 0) {
        this.insertSeparatorRow(sheet, rowIndex++, {
          A: 'Additions',
          C: 'Amount',
          D: 'Total',
        });

        this.populateRows(sheet, selectedOthersAdditions, rowIndex);
        rowIndex += selectedOthersAdditions.length;
        this.totalRow(sheet, rowIndex, {
          B: 'TOTAL ADDITION',
          D: this.payslip.currency?.value + totalOtherAddition.toString(),
        });
      }

      this.totalRow(sheet, rowIndex + 2, {
        B: 'NET PAY',
        D: this.payslip.currency?.value + netPay.toString(),
      });
      if (selectedEmployerLiabilities.length > 0) {
        rowIndex = rowIndex + 3;
        this.insertSeparatorRow(sheet, rowIndex++, {
          A: 'Employer Liabilities',
          C: 'Amount',
          D: 'Total',
        });
        this.populateRows(sheet, selectedEmployerLiabilities, rowIndex);
        rowIndex += selectedEmployerLiabilities.length;
        this.totalRow(sheet, rowIndex, {
          B: 'TOTAL AMOUNT',
          D: this.payslip.currency?.value + totalLiabilities.toString(),
        });
      }

      sheet.protect('password');
      // workbook.protection.enablePassword = true;
      const blob = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([blob]), 'Payslip.xlsx');
      this.onCancel();
    },
  },
};
</script>
<style scoped lang="scss">
::v-deep(.oxd-table-body) {
  grid-gap: 1px;
}
.gsl-hrm-personal-details {
  font-family: $oxd-font-family;
  font-size: 0.8rem;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  padding: 8px;
}
.detail-row {
  background: #f9f9f9;
  padding: 8px;
  border-radius: 4px;
}
</style>
